import type { LngLat, MapMouseEvent } from 'mapbox-gl'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import type { MapRef } from 'react-map-gl'
import { Layer, Source } from 'react-map-gl'

export interface SelectablePoint {
  id: string
  lat: number
  lng: number
}

export interface SerializableLatLng {
  lat: number
  lng: number
}

export interface SerializableLatLngBounds {
  northEast: SerializableLatLng
  southWest: SerializableLatLng
}

interface MapboxAreaSelectProps {
  mapRef: React.RefObject<MapRef | null>
  selectedMapArea: SerializableLatLngBounds | null
  setSelectedMapArea: (area: SerializableLatLngBounds | null) => void
  onClearSelection?: () => void
}

const SELECTED_AREA_FILL_COLOR = 'rgba(0, 128, 0, 0.1)'
const SELECTED_AREA_FILL_OUTLINE_COLOR = 'rgba(0, 128, 0, 0.6)'
const SELECTED_AREA_LINE_COLOR = 'rgba(0, 128, 0, 0.8)'
const SELECTED_AREA_LINE_WIDTH = 1

const buildBounds = (start: LngLat, end: LngLat): SerializableLatLngBounds => ({
  northEast: {
    lat: Math.max(start.lat, end.lat),
    lng: Math.max(start.lng, end.lng),
  },
  southWest: {
    lat: Math.min(start.lat, end.lat),
    lng: Math.min(start.lng, end.lng),
  },
})

const boundsToPolygon = (bounds: SerializableLatLngBounds) => {
  const { northEast, southWest } = bounds
  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [
        [
          [southWest.lng, southWest.lat],
          [northEast.lng, southWest.lat],
          [northEast.lng, northEast.lat],
          [southWest.lng, northEast.lat],
          [southWest.lng, southWest.lat],
        ],
      ],
    },
    properties: {},
  } as const
}

const MapboxAreaSelectRaw: FC<MapboxAreaSelectProps> = ({
  mapRef,
  selectedMapArea,
  setSelectedMapArea,
  onClearSelection,
}) => {
  const [draftBounds, setDraftBounds] =
    useState<SerializableLatLngBounds | null>(null)
  const isDraggingRef = useRef(false)
  const startPointRef = useRef<LngLat | null>(null)
  const skipNextClickRef = useRef(false)

  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!map) {
      return undefined
    }

    const handleMouseDown = (event: MapMouseEvent) => {
      if (!event.originalEvent.altKey) return

      event.preventDefault?.()
      event.originalEvent.stopPropagation?.()

      isDraggingRef.current = true
      startPointRef.current = event.lngLat
      map.dragPan.disable()
    }

    const handleMouseMove = (event: MapMouseEvent) => {
      if (!isDraggingRef.current || !startPointRef.current) return
      setDraftBounds(buildBounds(startPointRef.current, event.lngLat))
    }

    const finishSelection = (event: MapMouseEvent) => {
      if (!isDraggingRef.current || !startPointRef.current) return
      event.preventDefault?.()
      event.originalEvent.stopPropagation?.()

      isDraggingRef.current = false
      skipNextClickRef.current = true
      setDraftBounds(null)
      setSelectedMapArea(buildBounds(startPointRef.current, event.lngLat))
      startPointRef.current = null
      map.dragPan.enable()
    }

    const handleClick = (event: MapMouseEvent) => {
      if (event.originalEvent.altKey) return
      if (skipNextClickRef.current) {
        skipNextClickRef.current = false
        return
      }
      onClearSelection?.()
      setSelectedMapArea(null)
    }

    const handleWindowMouseUp = () => {
      if (!isDraggingRef.current) return
      isDraggingRef.current = false
      setDraftBounds(null)
      startPointRef.current = null
      map.dragPan.enable()
    }

    map.on('mousedown', handleMouseDown)
    map.on('mousemove', handleMouseMove)
    map.on('mouseup', finishSelection)
    map.on('click', handleClick)
    window.addEventListener('mouseup', handleWindowMouseUp)

    return () => {
      map.off('mousedown', handleMouseDown)
      map.off('mousemove', handleMouseMove)
      map.off('mouseup', finishSelection)
      map.off('click', handleClick)
      window.removeEventListener('mouseup', handleWindowMouseUp)
      map.dragPan.enable()
    }
  }, [mapRef, onClearSelection, setSelectedMapArea])

  const boundsToRender = draftBounds ?? selectedMapArea
  const polygon = useMemo(
    () => (boundsToRender ? boundsToPolygon(boundsToRender) : null),
    [boundsToRender]
  )

  if (!polygon) {
    return null
  }

  return (
    <Source id='selected-area' type='geojson' data={polygon}>
      <Layer
        id='selected-area-fill'
        type='fill'
        paint={{
          'fill-color': SELECTED_AREA_FILL_COLOR,
          'fill-outline-color': SELECTED_AREA_FILL_OUTLINE_COLOR,
        }}
      />
      <Layer
        id='selected-area-outline'
        type='line'
        paint={{
          'line-color': SELECTED_AREA_LINE_COLOR,
          'line-width': SELECTED_AREA_LINE_WIDTH,
        }}
      />
    </Source>
  )
}

export const MapboxAreaSelect = React.memo(MapboxAreaSelectRaw)
